<script setup lang="ts">
import useVuelidate from "@vuelidate/core";
import { email, required } from "~/lib/validator/validators";
import { getValidatorFieldProps } from "~/helpers/formHelpers";
import { getRoute } from "~/lib/routes";

definePageMeta({
	layout: "minimal",
	middleware: "guest"
});

const { formValues: authFormValues } = useAuthenticationForm();

const formValues = ref({
	email: authFormValues.value.email
});
const isLoading = ref(false);
const status = ref<"pending" | "success" | "error">("pending");
const { resetPassword } = useCustomerPassword();

const validator = useVuelidate(
	{
		email: { required, email }
	},
	formValues.value
);

const onSubmit = async (e: Event) => {
	e.preventDefault();

	isLoading.value = true;

	if (!(await validator.value.$validate())) {
		isLoading.value = false;
		return;
	}

	try {
		await resetPassword({ email: formValues.value.email, storefrontUrl: window.location.origin });
		status.value = "success";
	} catch {
		status.value = "error";
	} finally {
		isLoading.value = false;
	}
};
</script>

<template>
	<MinimalContainer>
		<div class="flex flex-col gap-8">
			<template v-if="status !== 'success'">
				<div class="flex flex-col gap-2">
					<Typography variant="h1">Wachtwoord vergeten?</Typography>
					<Typography variant="p">
						Geen probleem, dit kan iedereen overkomen. Vul hieronder je e-mailadres in wij sturen je een
						email waar je vervolgens je wachtwoord kan resetten.
					</Typography>
				</div>
				<div v-if="status === 'error'" class="bg-red p-4 rounded text-sm flex gap-2 items-center text-white">
					<SvgIcon name="info" class="min-w-[24px]" />
					Het is niet gelukt om een wachtwoord reset te versturen. Mogelijk is je e-mailadres niet bij ons
					bekend.
				</div>
				<form class="flex flex-col gap-4" @submit="onSubmit">
					<KippieInput
						v-model="formValues.email"
						label="E-mailadres"
						type="email"
						required
						name="email"
						v-bind="getValidatorFieldProps(validator, 'email')"
					/>
					<KippieButton block size="small" type="submit" :loading="isLoading">Doorgaan </KippieButton>
				</form>
			</template>
			<template v-else>
				<div class="flex flex-col gap-2">
					<Typography variant="h1">Je reset-link is onderweg</Typography>
					<Typography variant="p">
						Een e-mail is verzonden naar {{ formValues.email }}. Via de reset link kun je gemakkelijk je
						wachtwoord herstellen. Ontvang je binnen enkele minuten geen e-mail? Controleer dan je spam-box.
					</Typography>
				</div>

				<div class="flex flex-col gap-2">
					<NuxtLink :to="getRoute('assortment')">
						<KippieButton size="small" color="yellow" block>Naar ons assortiment</KippieButton>
					</NuxtLink>
					<NuxtLink :to="getRoute('home')">
						<KippieButton size="small" color="white" block>Naar de homepage</KippieButton>
					</NuxtLink>
				</div>
			</template>
		</div>

		<FamilyUspSection />
	</MinimalContainer>
</template>
